import type { components } from "./generated/trading-engine.js";

type Problem = components["schemas"]["Problem"];

export class TradingEngineError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly correlationId: string | null,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "TradingEngineError";
  }

  public get recoverable(): boolean {
    return this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

function isProblem(value: unknown): value is Problem {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<Problem>;
  return (
    typeof candidate.detail === "string" &&
    typeof candidate.status === "number" &&
    typeof candidate.code === "string"
  );
}

export function normalizeApiError(error: unknown, response: Response): TradingEngineError {
  const correlationHeader = response.headers.get("x-correlation-id");
  if (isProblem(error)) {
    return new TradingEngineError(
      error.detail,
      error.status,
      error.code,
      error.correlationId ?? correlationHeader,
      error.errors,
    );
  }
  return new TradingEngineError(
    response.status === 0 ? "TradingEngine is unreachable." : "TradingEngine request failed.",
    response.status,
    "REQUEST_FAILED",
    correlationHeader,
  );
}

export interface ApiResult<T> {
  readonly data?: T;
  readonly error?: unknown;
  readonly response: Response;
}

export function requireData<T>(result: ApiResult<T>): T {
  if (result.data !== undefined) return result.data;
  throw normalizeApiError(result.error, result.response);
}
